import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    IconDownload,
    IconLoader2,
    IconRefresh,
    IconSchool,
    IconSearch,
    IconShare2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import nationalUniversityResultService from '../../../../../services/nationalUniversityResultService';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    buildNationalUniversityResultShareUrl,
    canShareNationalUniversityResult,
    parseNationalUniversityResultShareParams,
} from './nationalUniversityResultShare';

const TOOL_PATH = '/tools/result/national-university-result';

const PROGRAMS = ['honours', 'degree', 'masters', 'professional'];

const EXAMS_BY_PROGRAM = {
    honours: [
        { code: '2201', key: 'h1' },
        { code: '2202', key: 'h2' },
        { code: '2203', key: 'h3' },
        { code: '2204', key: 'h4' },
        { code: '2205', key: 'hc' },
    ],
    degree: [
        { code: '1101', key: 'd1' },
        { code: '1102', key: 'd2' },
        { code: '1103', key: 'd3' },
        { code: '1104', key: 'dc' },
    ],
    masters: [
        { code: '3302', key: 'mf' },
        { code: '4301', key: 'mp' },
        { code: '3303', key: 'mi' },
    ],
};

const PROFESSIONAL_COURSES = [
    { code: '511', key: 'c511' },
    { code: '610', key: 'c610' },
    { code: '501', key: 'c501' },
    { code: '503', key: 'c503' },
    { code: '504', key: 'c504' },
    { code: '505', key: 'c505' },
    { code: '506', key: 'c506' },
    { code: '507', key: 'c507' },
    { code: '509', key: 'c509' },
    { code: '512', key: 'c512' },
    { code: '513', key: 'c513' },
    { code: '502', key: 'c502' },
    { code: '508', key: 'c508' },
    { code: '601', key: 'c601' },
    { code: '606', key: 'c606' },
    { code: '640', key: 'c640' },
    { code: '510', key: 'c510' },
    { code: '8', key: 'c8' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1999 }, (_, index) => String(currentYear - index));

const fieldClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] font-semibold text-[#3D3833] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

function normalizeNumericInput(value) {
    return String(value)
        .replace(/[০-৯]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0x09e6 + 48))
        .replace(/\D/g, '');
}

function FieldLabel({ htmlFor, children, required = true }) {
    return (
        <label htmlFor={htmlFor} className="mb-1.5 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-300">
            {children}{required ? <span className="text-red-500"> *</span> : null}
        </label>
    );
}

export default function NationalUniversityResult() {
    const { t } = useTranslation();
    const location = useLocation();
    const mountedRef = useRef(true);
    const pdfUrlRef = useRef('');
    const [form, setForm] = useState({
        program: '',
        course: '',
        examination: '',
        year: '',
        roll: '',
        reg: '',
        captcha: '',
    });
    const [sessionToken, setSessionToken] = useState('');
    const [captchaQuestion, setCaptchaQuestion] = useState('');
    const [captchaLoading, setCaptchaLoading] = useState(false);
    const [professionalExams, setProfessionalExams] = useState([]);
    const [examsLoading, setExamsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [error, setError] = useState('');

    const clearPdf = useCallback(() => {
        if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = '';
        }
        setPdfUrl('');
    }, []);

    const update = (key, value) => {
        setForm((previous) => ({ ...previous, [key]: value }));
        if (result) setResult(null);
        if (pdfUrl) clearPdf();
        if (error) setError('');
    };

    const loadCaptcha = useCallback(async (program, token = '', preserveError = false) => {
        if (!program) {
            setCaptchaQuestion('');
            setSessionToken('');
            setCaptchaLoading(false);
            return;
        }
        setCaptchaLoading(true);
        if (!preserveError) setError('');
        try {
            const data = await nationalUniversityResultService.getCaptcha(program, token);
            if (!mountedRef.current) return;
            setCaptchaQuestion(data.question || '');
            setSessionToken(data.sessionToken || '');
            const autoAnswer = String(data.answer || '').trim()
                || (() => {
                    const match = String(data.question || '').match(/(\d+)\s*\+\s*(\d+)/);
                    return match ? String(Number(match[1]) + Number(match[2])) : '';
                })();
            setForm((previous) => ({ ...previous, captcha: autoAnswer }));
        } catch (requestError) {
            if (token && requestError?.code === 'RESULT_SESSION_EXPIRED') {
                await loadCaptcha(program, '');
                return;
            }
            if (!mountedRef.current) return;
            setCaptchaQuestion('');
            setSessionToken('');
            setError(requestError?.message || t('nationalUniversityResult.errors.captchaLoad'));
        } finally {
            if (mountedRef.current) setCaptchaLoading(false);
        }
    }, [t]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearPdf();
        };
    }, [clearPdf]);

    useEffect(() => {
        const search = location.search;
        if (!search) return;
        const parsed = parseNationalUniversityResultShareParams(search);
        if (!parsed.program && !parsed.examination && !parsed.year && !parsed.reg) return;
        setForm((previous) => ({
            ...previous,
            program: parsed.program || previous.program,
            course: parsed.course || previous.course,
            examination: parsed.examination || previous.examination,
            year: parsed.year || previous.year,
            roll: parsed.roll || previous.roll,
            reg: parsed.reg || previous.reg,
            captcha: '',
        }));
        setResult(null);
        clearPdf();
        setError('');
    }, [location.search, clearPdf]);

    useEffect(() => {
        if (!form.program) return;
        void loadCaptcha(form.program);
    }, [form.program]); // eslint-disable-line react-hooks/exhaustive-deps -- reload captcha when program changes

    useEffect(() => {
        if (form.program !== 'professional' || !form.course) {
            setProfessionalExams([]);
            return undefined;
        }

        let cancelled = false;
        setExamsLoading(true);
        nationalUniversityResultService.getProfessionalExams(form.course)
            .then((data) => {
                if (cancelled || !mountedRef.current) return;
                setProfessionalExams(Array.isArray(data.exams) ? data.exams : []);
            })
            .catch((requestError) => {
                if (cancelled || !mountedRef.current) return;
                setProfessionalExams([]);
                setError(requestError?.message || t('nationalUniversityResult.errors.examsLoad'));
            })
            .finally(() => {
                if (!cancelled && mountedRef.current) setExamsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [form.program, form.course, t]);

    const examOptions = useMemo(() => {
        if (form.program === 'professional') {
            return professionalExams.map((exam) => ({
                code: exam.code,
                label: exam.name,
            }));
        }
        return (EXAMS_BY_PROGRAM[form.program] || []).map((exam) => ({
            code: exam.code,
            label: t(`nationalUniversityResult.exams.${exam.key}`),
        }));
    }, [form.program, professionalExams, t]);

    const hasInput = Boolean(
        form.program || form.course || form.examination || form.year || form.roll || form.reg || form.captcha,
    );
    const canSubmit = Boolean(
        form.program
        && form.examination
        && form.year
        && form.reg
        && form.captcha
        && sessionToken
        && !captchaLoading
        && (form.program !== 'professional' || form.course),
    );

    const status = deriveToolStatus({
        hasInput,
        canRun: canSubmit,
        processing: loading,
        hasResult: Boolean(result),
        hasError: Boolean(error),
    });
    const statusText = getToolStatusText(status, t, {
        ready: t('nationalUniversityResult.statusReady'),
        processing: t('nationalUniversityResult.statusProcessing'),
        success: t('nationalUniversityResult.statusSuccess'),
        error: t('nationalUniversityResult.statusError'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((step) => t(`nationalUniversityResult.howTo.step${step}`)),
        [t],
    );

    const handleProgramChange = (program) => {
        setForm({
            program,
            course: '',
            examination: '',
            year: form.year,
            roll: form.roll,
            reg: form.reg,
            captcha: '',
        });
        setProfessionalExams([]);
        setResult(null);
        clearPdf();
        setError('');
    };

    const handleCourseChange = (course) => {
        setForm((previous) => ({ ...previous, course, examination: '', captcha: previous.captcha }));
        setResult(null);
        clearPdf();
        setError('');
    };

    const handleSubmit = async () => {
        if (!canSubmit || loading) return;
        setLoading(true);
        setError('');
        setResult(null);
        clearPdf();
        recordToolUsage('result/national-university-result', { category: 'result' });

        try {
            const data = await nationalUniversityResultService.fetchResult({
                program: form.program,
                course: form.course,
                examination: form.examination,
                year: form.year,
                roll: form.roll,
                reg: form.reg,
                captcha: form.captcha,
                sessionToken,
            });

            if (data?.pdfBase64) {
                const binary = atob(data.pdfBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
                const blob = new Blob([bytes], { type: data.contentType || 'application/pdf' });
                const url = URL.createObjectURL(blob);
                pdfUrlRef.current = url;
                setPdfUrl(url);
            }

            setResult(data);
            notifications.show({
                title: t('common.success'),
                message: t('nationalUniversityResult.resultFound'),
                color: 'green',
            });
            void loadCaptcha(form.program);
        } catch (requestError) {
            setError(requestError?.message || t('nationalUniversityResult.errors.fetch'));
            const refreshedToken = requestError?.data?.sessionToken || sessionToken;
            if (
                requestError?.code === 'CAPTCHA_FAILED'
                || requestError?.code === 'RESULT_SESSION_EXPIRED'
            ) {
                void loadCaptcha(
                    form.program,
                    requestError?.code === 'RESULT_SESSION_EXPIRED' ? '' : refreshedToken,
                    true,
                );
            } else {
                void loadCaptcha(form.program, '', true);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setForm({
            program: '',
            course: '',
            examination: '',
            year: '',
            roll: '',
            reg: '',
            captcha: '',
        });
        setProfessionalExams([]);
        setResult(null);
        clearPdf();
        setError('');
        setCaptchaQuestion('');
        setSessionToken('');
        window.history.replaceState(null, '', TOOL_PATH);
    };

    const handleDownloadPdf = () => {
        if (!pdfUrl || !result) return;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `sohozkaj-nu-result-${form.reg || Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        notifications.show({
            title: t('common.success'),
            message: t('nationalUniversityResult.pdfDownloaded'),
            color: 'green',
        });
    };

    const handleShare = async () => {
        if (!canShareNationalUniversityResult(form)) {
            notifications.show({
                title: t('common.error'),
                message: t('nationalUniversityResult.shareRequiresInput'),
                color: 'yellow',
            });
            return;
        }

        const url = buildNationalUniversityResultShareUrl(window.location.origin, TOOL_PATH, form);
        const copied = copyTextToClipboard(url);

        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({
                    title: t('nationalUniversityResult.shareTitle'),
                    text: t('nationalUniversityResult.shareText'),
                    url,
                });
                notifications.show({
                    title: t('common.success'),
                    message: t('nationalUniversityResult.shared'),
                    color: 'green',
                });
                return;
            } catch (err) {
                if (err?.name === 'AbortError') return;
            }
        }

        if (copied) {
            notifications.show({
                title: t('common.success'),
                message: t('nationalUniversityResult.linkCopied'),
                color: 'green',
            });
        } else {
            notifications.show({
                title: t('common.error'),
                message: t('nationalUniversityResult.shareFailed'),
                color: 'red',
            });
        }
    };

    return (
        <ToolPageLayout
            slug="result/national-university-result"
            subtitleKey="nationalUniversityResult.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('nationalUniversityResult.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasInput || result ? handleReset : undefined}
            resetLabel={t('nationalUniversityResult.reset')}
            showTrust={false}
            primaryAction={{
                label: loading ? t('nationalUniversityResult.fetching') : t('nationalUniversityResult.getResult'),
                onClick: handleSubmit,
                disabled: !canSubmit || loading,
                loading,
                icon: loading ? <IconLoader2 size={18} className="animate-spin" /> : <IconSearch size={18} />,
            }}
            primaryActionPlacement="beforeBelowWorkArea"
            controls={(
                <div className="flex flex-col gap-3.5">
                    <p className="text-[13px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('nationalUniversityResult.settingsHint')}
                    </p>

                    <div>
                        <FieldLabel htmlFor="nu-program">{t('nationalUniversityResult.fields.program')}</FieldLabel>
                        <select
                            id="nu-program"
                            className={fieldClass}
                            value={form.program}
                            onChange={(event) => handleProgramChange(event.target.value)}
                        >
                            <option value="">{t('nationalUniversityResult.placeholders.program')}</option>
                            {PROGRAMS.map((program) => (
                                <option key={program} value={program}>
                                    {t(`nationalUniversityResult.programs.${program}`)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {form.program === 'professional' ? (
                        <div>
                            <FieldLabel htmlFor="nu-course">{t('nationalUniversityResult.fields.course')}</FieldLabel>
                            <select
                                id="nu-course"
                                className={fieldClass}
                                value={form.course}
                                onChange={(event) => handleCourseChange(event.target.value)}
                            >
                                <option value="">{t('nationalUniversityResult.placeholders.course')}</option>
                                {PROFESSIONAL_COURSES.map((course) => (
                                    <option key={course.code} value={course.code}>
                                        {t(`nationalUniversityResult.courses.${course.key}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : null}

                    <div>
                        <FieldLabel htmlFor="nu-exam">{t('nationalUniversityResult.fields.examination')}</FieldLabel>
                        <select
                            id="nu-exam"
                            className={fieldClass}
                            value={form.examination}
                            disabled={!form.program || (form.program === 'professional' && (examsLoading || !form.course))}
                            onChange={(event) => update('examination', event.target.value)}
                        >
                            <option value="">
                                {examsLoading
                                    ? t('nationalUniversityResult.examsLoading')
                                    : t('nationalUniversityResult.placeholders.examination')}
                            </option>
                            {examOptions.map((exam) => (
                                <option key={exam.code} value={exam.code}>
                                    {exam.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <FieldLabel htmlFor="nu-year">{t('nationalUniversityResult.fields.year')}</FieldLabel>
                        <select
                            id="nu-year"
                            className={fieldClass}
                            value={form.year}
                            onChange={(event) => update('year', event.target.value)}
                        >
                            <option value="">{t('nationalUniversityResult.placeholders.year')}</option>
                            {YEARS.map((year) => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <FieldLabel htmlFor="nu-roll" required={false}>
                            {t('nationalUniversityResult.fields.roll')}
                        </FieldLabel>
                        <input
                            id="nu-roll"
                            className={fieldClass}
                            inputMode="numeric"
                            value={form.roll}
                            onChange={(event) => update('roll', normalizeNumericInput(event.target.value))}
                            placeholder={t('nationalUniversityResult.placeholders.roll')}
                            maxLength={10}
                        />
                    </div>

                    <div>
                        <FieldLabel htmlFor="nu-reg">{t('nationalUniversityResult.fields.registration')}</FieldLabel>
                        <input
                            id="nu-reg"
                            className={fieldClass}
                            inputMode="numeric"
                            value={form.reg}
                            onChange={(event) => update('reg', normalizeNumericInput(event.target.value))}
                            placeholder={t('nationalUniversityResult.placeholders.registration')}
                            maxLength={14}
                        />
                    </div>

                    <div>
                        <FieldLabel htmlFor="nu-captcha">{t('nationalUniversityResult.fields.captcha')}</FieldLabel>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => void loadCaptcha(form.program, sessionToken)}
                                disabled={!form.program || captchaLoading}
                                className="inline-flex h-[46px] w-[46px] flex-none items-center justify-center rounded-xl border border-[#ECE8E4] bg-white text-[#3D3833] transition-colors hover:border-primary/40 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                                aria-label={t('nationalUniversityResult.reloadCaptcha')}
                                title={t('nationalUniversityResult.reloadCaptcha')}
                            >
                                <IconRefresh size={16} className={captchaLoading ? 'animate-spin' : ''} />
                            </button>
                            <div className="flex h-[46px] min-w-[180px] flex-none items-center justify-center rounded-xl border border-[#ECE8E4] bg-[#FAFAF8] px-5 dark:border-zinc-700 dark:bg-zinc-950">
                                {captchaLoading ? (
                                    <span className="text-[12px] text-[#8C847D]">…</span>
                                ) : captchaQuestion ? (
                                    <span className="whitespace-nowrap text-[16px] font-bold tracking-wide text-[#1C1815] dark:text-zinc-100">
                                        {captchaQuestion} =
                                    </span>
                                ) : (
                                    <span className="text-[12px] text-[#8C847D]">—</span>
                                )}
                            </div>
                            <input
                                id="nu-captcha"
                                className={`${fieldClass} min-w-0 flex-1`}
                                inputMode="numeric"
                                value={form.captcha}
                                onChange={(event) => update('captcha', normalizeNumericInput(event.target.value))}
                                placeholder={t('nationalUniversityResult.placeholders.captcha')}
                                maxLength={4}
                                disabled={!form.program}
                            />
                        </div>
                    </div>

                    <p className="text-[12px] leading-relaxed text-[#8C847D] dark:text-zinc-500">
                        {t('nationalUniversityResult.sourceNote')}
                    </p>
                </div>
            )}
            belowWorkArea={error ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13.5px] text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
                    {error}
                </div>
            ) : null}
            settings={result ? (
                <ToolCard title={t('nationalUniversityResult.exportShareTitle')}>
                    <div className="flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={handleDownloadPdf}
                            disabled={!pdfUrl}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3.5 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                            <IconDownload size={16} />
                            {t('nationalUniversityResult.downloadPdf')}
                        </button>
                        <button
                            type="button"
                            onClick={handleShare}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-2.5 text-[13px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                        >
                            <IconShare2 size={16} />
                            {t('nationalUniversityResult.share')}
                        </button>
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('nationalUniversityResult.previewTitle')}
                    status={status}
                    idleIcon={<IconSchool size={28} stroke={1.6} />}
                    idleTitle={t('nationalUniversityResult.previewEmpty')}
                    idleHint={t('nationalUniversityResult.previewEmptyHint')}
                    processingLabel={t('nationalUniversityResult.fetching')}
                >
                    {result && pdfUrl ? (
                        <div className="flex h-full min-h-[520px] flex-col gap-3">
                            <div className="rounded-xl border border-orange-200 bg-orange-50 px-3.5 py-3 dark:border-orange-900/40 dark:bg-orange-950/20">
                                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-orange-800 dark:text-orange-300">
                                    {t('nationalUniversityResult.resultReady')}
                                </p>
                                <p className="mt-1 text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                    {t(`nationalUniversityResult.programs.${result.program || form.program}`)}
                                    {result.year ? ` · ${result.year}` : ''}
                                    {result.reg ? ` · ${result.reg}` : ''}
                                </p>
                            </div>
                            <iframe
                                title={t('nationalUniversityResult.previewTitle')}
                                src={pdfUrl}
                                className="min-h-[480px] w-full flex-1 rounded-xl border border-[#ECE8E4] bg-white dark:border-zinc-700"
                            />
                            <p className="text-center text-[12px] text-[#8C847D] dark:text-zinc-500">
                                {t('nationalUniversityResult.officialSource')}
                            </p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
